<template>
  <div>
    <sd-section-title :title="t('st_title')" :note="t('st_note')" style="margin-top:26px" />
    <div v-if="!ST" class="sd-card sd-warnbox">⚠️ {{ t('st_missing') }}</div>
    <template v-else>
      <section class="sd-kpis" style="margin-top:18px">
        <sd-kpi-card gold :label="t('st_k_house')" icon="mdi-emoticon-happy-outline" :value="h.overall.pos + '%'" color="var(--sd-pos-2)" :spark="h.years.map(y => y.pos)" spark-color="var(--sd-gold-500)" :delta="sh.hDelta" :footnote="sh.houseFt" />
        <sd-kpi-card :label="t('st_k_muni')" icon="mdi-check-circle-outline" :value="sh.mL.pos + '%'" :color="stC(sh.mL.pos)" :spark="m.years.map(y => y.pos)" :delta="sh.mDelta" :footnote="sh.muniFt" />
        <sd-kpi-card :label="t('st_k_prio')" icon="mdi-gauge" :footnote="sh.prioFt">
          <span style="font-size:19px;line-height:1.25;display:block">{{ stN(sh.lowItem, lang) }}</span><span class="num-ltr" style="color:var(--sd-crit)">{{ sh.lowItem.pos }}%</span>
        </sd-kpi-card>
        <sd-kpi-card :label="t('st_k_gap')" icon="mdi-map-marker-outline" :value="String(sh.gap)" :unit="t('ex_pts')" :color="sh.gapC" :footnote="sh.gapFt" />
      </section>
      <section class="sd-grid" style="margin-top:18px">
        <div class="sd-card c7">
          <div class="head">
<div><h3>{{ t('st_c_prio') }}</h3><p class="hint">{{ t('st_c_prio_h', { a: m.from, b: m.to }) }}</p></div>
            <div class="sd-legend" style="margin:0"><span class="lg"><span class="sw" style="background:var(--sd-pos)" />{{ t('positive') }}</span><span class="lg"><span class="sw" style="background:var(--sd-neu)" />{{ t('neu') }}</span><span class="lg"><span class="sw" style="background:var(--sd-neg)" />{{ t('negative') }}</span><span class="lg" style="color:var(--sd-ink-4)">Δ {{ t('st_d24') }}</span></div>
</div>
          <sd-diverging-rows :items="prioRows" with-delta />
        </div>
        <div class="sd-card c5">
          <div class="head"><div><h3>{{ t('st_c_aman') }}</h3><p class="hint">{{ t('st_c_aman_h', { a: m.from, b: m.to }) }}</p></div></div>
          <sd-meter-rows :items="amanRows" />
        </div>
        <div class="sd-card c4">
          <div class="head"><div><h3>{{ t('st_c_axes') }}</h3><p class="hint">{{ t('st_c_axes_h') }} · {{ yrs.join(' · ') }}</p></div></div>
          <sd-grouped-bars :groups="m.axes.map(a => ({ label: stN(a, lang), color: AX_C[a.key], vals: a.byYear }))" :keys="yrs" />
        </div>
        <div class="sd-card c4">
          <div class="head"><div><h3>{{ t('st_c_prod') }}</h3><p class="hint">{{ t('st_c_prod_h') }} · {{ fmt(h.n) }} {{ t('st_benef') }}</p></div></div>
          <sd-meter-rows :items="prodRows" />
        </div>
        <div class="sd-card c4">
          <div class="head"><div><h3>{{ t('st_c_htrend') }}</h3><p class="hint">{{ t('st_c_htrend_h') }} · {{ h.from }}–{{ h.to }}</p></div></div>
          <sd-line-chart :series="hTrend" :years="h.years.map(y => String(y.year))" :lo="80" :hi="95" labels emph-last emph-color="var(--sd-pos-2)" :height="190" />
        </div>
        <div class="sd-card c6">
          <div class="head"><div><h3>{{ t('st_c_journey') }}</h3><p class="hint">{{ t('st_c_journey_h', { n: fmt(h.journey.n), p: pctS(h.journey.shareOfAll) }) }}</p></div></div>
          <sd-diverging-rows :items="journeyRows" />
          <div style="font-size:12.5px;font-weight:700;color:var(--sd-ink-2);margin-top:16px">{{ t('st_contact') }}</div>
          <div class="cstat">
            <div><div class="n num-ltr" style="color:var(--sd-ink)">{{ h.contact.yesPct }}%</div><div class="t">{{ t('st_contacted') }} (n={{ fmt(h.contact.asked) }})</div></div>
            <div><div class="n num-ltr" style="color:var(--sd-warn)">{{ sh.complaint.share || 0 }}%</div><div class="t">{{ t('st_complaints') }} (n={{ fmt(h.contact.yes) }})</div></div>
            <div><div class="n num-ltr" :style="{color: stC(h.contact.sat.pos)}">{{ h.contact.sat.pos }}%</div><div class="t">{{ t('st_csat') }}</div></div>
            <div><div class="n num-ltr" style="color:var(--sd-crit)">{{ sh.complaint.pos || 0 }}%</div><div class="t">{{ t('st_csat_cmp') }} (n={{ fmt(sh.complaint.count || 0) }})</div></div>
          </div>
        </div>
        <div class="sd-card c6">
          <div class="head"><div><h3>{{ t('st_c_ins') }}</h3><p class="hint">{{ t('st_c_ins_h') }}</p></div><span class="ai-badge">✦ {{ t('ai_b') }}</span></div>
          <sd-insight-list :items="insights" />
        </div>
      </section>
      <div class="sd-hint" style="margin-top:18px">{{ t('st_src') }}: {{ (ST.sources || []).join(' · ') }} · {{ ST.generatedAt }}</div>
    </template>
  </div>
</template>
<script>
import { sdMixin } from '../store.js'
import { fmt, pctS, pctHtml, stC, d1 } from '../lib/format.js'
import { strategicHeadline, strategicInsights, stN, AX_C } from '../lib/strategic.js'
import SdSectionTitle from '../components/SectionTitle.vue'
import SdKpiCard from '../components/KpiCard.vue'
import SdDivergingRows from '../components/DivergingRows.vue'
import SdMeterRows from '../components/MeterRows.vue'
import SdGroupedBars from '../components/charts/GroupedBars.vue'
import SdLineChart from '../components/charts/LineChart.vue'
import SdInsightList from '../components/InsightList.vue'
export default {
  name: 'SdStrategicView',
  components: { SdSectionTitle, SdKpiCard, SdDivergingRows, SdMeterRows, SdGroupedBars, SdLineChart, SdInsightList },
  mixins: [sdMixin],
  data: () => ({ AX_C }),
  computed: {
    sh() { return strategicHeadline(this.ST, this.lang, this.t) },
    h() { return this.ST.housing }, m() { return this.ST.municipal },
    yrs() { return this.m.years.map((y) => String(y.year)) },
    prioRows() {
      const mF = this.sh.mF, mL = this.sh.mL
      return this.m.items.slice().sort((a, b) => a.pos - b.pos).map((o) => { const a = o.byYear[String(mF.year)], b = o.byYear[String(mL.year)]
        return { key: o.id, name: stN(o, this.lang), pos: o.pos, nu: o.nu, neg: o.neg, tag: { t: stN(this.sh.axOf(o.axis), this.lang), c: AX_C[o.axis] }, delta: a && b ? d1(b.pos - a.pos) : null, tip: this.dvTip(o) } })
    },
    amanRows() { return this.m.amanahs.map((r) => ({ key: r.ar, name: stN(r, this.lang), pct: r.pos, color: stC(r.pos), sub: `${fmt(r.respondents)} ${this.t('st_resp')}`, tip: this.dvTip(r) })) },
    prodRows() { return this.h.products.slice().sort((a, b) => b.pos - a.pos).map((r) => ({ key: r.ar, name: stN(r, this.lang), pct: r.pos, color: 'var(--sd-gold-500)', tip: this.dvTip(r) })) },
    journeyRows() { return this.h.journey.items.slice().sort((a, b) => b.neg - a.neg).map((o) => ({ key: o.ar, name: stN(o, this.lang), pos: o.pos, nu: o.nu, neg: o.neg, tip: this.dvTip(o) })) },
    hTrend() { const v = {}, tips = {}; this.h.years.forEach((y) => { v[y.year] = y.pos; tips[y.year] = `<b>${y.year}</b><br><span class="tt-k">${this.t('positive')}:</span> ${pctHtml(y.pos)}<br><span class="tt-k">${this.t('negative')}:</span> ${pctHtml(y.neg)}<br><span class="tt-k">n:</span> ${fmt(y.n)}` }); return [{ name: this.t('st_k_house'), color: 'var(--sd-gold-500)', v, tips }] },
    insights() { return strategicInsights(this.ST, this.lang) },
  },
  methods: {
    fmt, pctS, stC, stN,
    dvTip(o) { return `<b>${stN(o, this.lang)}</b><br><span class="tt-k">${this.t('positive')}:</span> ${pctHtml(o.pos)}<br><span class="tt-k">${this.t('neu')}:</span> ${pctHtml(o.nu)}<br><span class="tt-k">${this.t('negative')}:</span> ${pctHtml(o.neg)}<br><span class="tt-k">n:</span> ${fmt(o.n)}` },
  },
}
</script>
